import { BrandedAttributeItem } from '@ui/components/BrandedFares/BrandedCard/BrandedCard.interfaces';
import { t } from '@shared/helpers/translation';
import { BaggageType } from '@shared/interfaces/baggage';
import {
    BaggageIncluded,
    FlightBrandedAttributeStatus,
    FlightBrandedAttributeType,
} from '@shared/interfaces/brandedFares';
import { FlightBrandedFareAttribute, FlightLeg } from '@/api/Cart/cart.interfaces';
import {
    FlightBrandedFare,
    FlightBrandedFareAttribute as BookingFlightBrandedFareAttribute,
    IncludedBaggage,
} from '@/api/MyBooking/myBooking.interfaces';
import { getBookingIncludedBaggageLabel } from './baggageBooking';
import {
    getIncludedBaggageLabel,
    getIncludedBaggagesFromLegs,
} from '@shared/helpers/baggage';

type CheckoutAttribute = {
    dataType: 'checkout';
    attributes: FlightBrandedFareAttribute[];
    flightLegs: FlightLeg[];
};

type BookingAttribute = {
    dataType: 'booking';
    brandedFare: FlightBrandedFare | null;
};

type AttributeInput = CheckoutAttribute | BookingAttribute;

export const mapBrandedFareAttributes = (data: AttributeInput): BrandedAttributeItem[] => {
    switch (data.dataType) {
        case 'checkout': {
            const baggage = getCheckoutBaggage(data.flightLegs);
            return data.attributes.flatMap((attr) => mapCheckoutAttribute(attr, baggage));
        }

        case 'booking': {
            if (!data.brandedFare?.attributes) return [];

            const baggage = getBookingBaggage(data.brandedFare.includedBaggage);
            if (!baggage) return [];

            return data.brandedFare.attributes.flatMap((attr) =>
                mapBookingAttribute(attr, baggage)
            );
        }
    }
};

const mapCheckoutAttribute = (
    attribute: FlightBrandedFareAttribute,
    baggage: BaggageIncluded
): BrandedAttributeItem[] => {
    return getAttributeDescriptions(
        { type: attribute.Type, status: attribute.Status },
        baggage
    ).map((description) => ({
        status: attribute.Status,
        description,
    }));
};

const mapBookingAttribute = (
    attribute: BookingFlightBrandedFareAttribute,
    baggage: BaggageIncluded
): BrandedAttributeItem[] => {
    return getAttributeDescriptions(attribute, baggage).map((description) => ({
        status: attribute.status,
        description,
    }));
};

export const getCheckoutBaggage = (legs: FlightLeg[]): BaggageIncluded => {
    const bagsIncluded = getIncludedBaggagesFromLegs(legs);

    return {
        carryOnBaggage: bagsIncluded.baggages
            .filter((b) => [BaggageType.CabinBag, BaggageType.SmallBag].includes(b.Type))
            .map((bag) => getIncludedBaggageLabel(bag, bagsIncluded.splitSector, true)),

        checkedBag: bagsIncluded.baggages
            .filter((b) => b.Type === BaggageType.CheckedBag)
            .map((bag) => getIncludedBaggageLabel(bag, bagsIncluded.splitSector, true)),
    };
};

export const getBookingBaggage = (
    includedBaggage: IncludedBaggage | undefined
): BaggageIncluded | null => {
    if (!includedBaggage) return null;

    return {
        carryOnBaggage: includedBaggage.cabin
            ? [
                  getBookingIncludedBaggageLabel(
                      includedBaggage.cabin,
                      BaggageType.CabinBag,
                      true
                  ),
              ]
            : [],
        checkedBag: includedBaggage.checkedIn
            ? [
                  getBookingIncludedBaggageLabel(
                      includedBaggage.checkedIn,
                      BaggageType.CheckedBag,
                      true
                  ),
              ]
            : [],
    };
};

const getAttributeDescriptions = (
    attribute: {
        type: FlightBrandedAttributeType;
        status: FlightBrandedAttributeStatus;
    },
    baggage: BaggageIncluded | null
): string[] => {
    if (!baggage) return [];

    const { type, status } = attribute;

    if (status === FlightBrandedAttributeStatus.Included) {
        switch (type) {
            case FlightBrandedAttributeType.CarryOn:
                return baggage.carryOnBaggage;

            case FlightBrandedAttributeType.CheckedBag:
            case FlightBrandedAttributeType.ChauffeurTransfer:
                return baggage.checkedBag;
        }
    }

    return [
        t({
            key: `branded_fares.attributes.${type}.${status}`,
        }),
    ];
};
